/**
 * Generazione slot da Calendario (template + modifiche) e assegnazione automatica.
 * L'assegnazione usa il microservizio Python (OR-Tools CP-SAT) per ottimizzazione combinatoria.
 */

import { getState, getFullDayAbsenceDates, getAbsenceDatesForExclusion, getSlotBlocksForDoctorDate } from './store.js';
import { canAssignDoctorToSlot } from './validation.js';
import { TIME_SLOTS, HOURS_PER_SLOT, HOURS_PER_NIGHT, HOURS_PER_REPERIBILITA, getHoursForSlot, getSlotWeight, getTemplateWeekdays, CATEGORY_TO_UOC } from './types.js';
import {
  computeDoctorResidualDebtForPeriod,
  computeSpreadBaselineForPeriod,
  computeCaricoTargetForPeriod,
  computeLockedWorkSlotsByDoctorWeek,
  computeWeekendHoursOverview,
  computeWeekendPresenceHistoryByDoctor,
  getWeekStartsInRange,
  getWeekStart,
  getWeekEnd,
} from './workload.js';
import {
  getDaysInMonth,
  getFestiviInfrasettimanali,
  getDaysInWeekRange,
  ensurePlannerSlotName,
  slotLogicalKeyString,
  stableCalendarSlotKey,
  addCalendarDays,
} from './utils.js';

export { slotLogicalKeyString } from './utils.js';

/** Budget proxy (ms) da /api/status o fallback 30 min — allineato a TURNI_SOLVER_PROXY_TIMEOUT_SEC. */
function solverProxyBudgetMs() {
  if (typeof window !== 'undefined') {
    const fromStatus = Number(window.TURNI_SOLVER_PROXY_TIMEOUT_MS);
    if (fromStatus > 0) return fromStatus;
    const legacy = Number(window.TURNI_SOLVER_TIMEOUT_MS);
    if (legacy > 0) return legacy;
  }
  return 1_800_000;
}

function solverTimeoutMinutesLabel() {
  return Math.max(1, Math.round(solverProxyBudgetMs() / 60_000));
}

export function solver504Message(elapsedMs) {
  const min = solverTimeoutMinutesLabel();
  const proxyMs = solverProxyBudgetMs();
  const elapsed = Number(elapsedMs) || 0;
  const likelyNpm90s =
    elapsed > 0 && elapsed < Math.min(proxyMs * 0.25, 180_000);
  const npmHint = likelyNpm90s
    ? ' Il 504 dopo 1–2 minuti indica quasi sempre Nginx Proxy Manager (default ~90s): sul Pi2 eseguire `bash scripts/npm_apply_solver_proxy_timeout.sh` o incollare deploy/npm-proxy-advanced-timeout.conf nel tab Advanced del proxy turnialfa.'
    : ' Verificare anche il timeout del reverse proxy (Nginx/NPM).';
  return `Il solver ha impiegato troppo tempo (timeout server/proxy). Per infrasettimanali su più settimane il budget configurato è circa ${min} minuti;${npmHint}`;
}

/** Timeout fetch solver: allineato al budget proxy (default 30 min per infra). */
export function solverRequestTimeoutMs(slotCount, phase = null) {
  const proxyMs = solverProxyBudgetMs();
  const n = Number(slotCount) || 0;
  const ph = (phase || '').toLowerCase();
  if (ph === 'infrasettimanali') {
    return proxyMs;
  }
  if (n > 300) return Math.min(proxyMs, 600_000);
  if (n > 100) return Math.min(proxyMs, 300_000);
  return Math.min(proxyMs, 120_000);
}

function slotPersistLayerRank(s) {
  if (s.syntheticFromAssignment) return 0;
  const key = String(s.slotKey || s.id || '');
  if (key.includes('-lgk-') || key.endsWith('-tpl') || key.endsWith('-mod')) return 1;
  return 2;
}

/**
 * Tra più oggetti slot con la stessa chiave logica, sceglie una riga:
 * priorità assegnazione sulla chiave esatta, poi strato persistito (DB),
 * poi calendario sintetico (-tpl/-mod), poi sintetico da assignment.
 */
function pickRepresentativeSlotForLogicalGroup(slots, byAssignments) {
  if (!slots?.length) return null;
  if (slots.length === 1) return slots[0];
  const score = (s) => {
    const k = String(s.slotKey || s.id || '');
    const hasAsn = Boolean(k && byAssignments && byAssignments[k]);
    return (hasAsn ? 1000 : 0) + slotPersistLayerRank(s) * 10;
  };
  const sorted = [...slots].sort((a, b) => {
    const d = score(b) - score(a);
    if (d !== 0) return d;
    return String(a.id || a.slotKey || '').localeCompare(String(b.id || b.slotKey || ''));
  });
  const rep = sorted[0];
  const oneOffBuddy = sorted.find((s) => s && s._oneOffModId);
  /** Il rappresentante preferito può essere riga DB senza campo calendario: conserva il mod id spot. */
  if (oneOffBuddy && !(rep && rep._oneOffModId)) {
    return { ...rep, _oneOffModId: oneOffBuddy._oneOffModId };
  }
  return rep;
}

/**
 * Costruisce gli slot del mese da template + modifiche
 */

/** YYYY-MM-DD per confronti con date generate in calendario locale */
function calendarDayOnly(isoOrDate) {
  if (!isoOrDate) return '';
  return String(isoOrDate).slice(0, 10);
}

export function buildSlotsFromCalendar(year, month) {
  const state = getState();
  const { calendarTemplates, calendarModifications } = state;
  const days = getDaysInMonth(year, month);
  const festivi = getFestiviInfrasettimanali(year);
  const slots = [];
  const slotKeys = new Set(); // per evitare duplicati

  const isAllowedOnFestivo = (t) => {
    const isTI = t.category === 'terapia_intensiva';
    const isRep = t.timeSlot === 'reperibilita_notte' || t.timeSlot === 'reperibilita_giorno' || t.activityType === 'reperibilita';
    // Festivi infrasettimanali: manteniamo solo TI e reperibilità.
    // Le altre discipline infrasettimanali (SO/NORA/MD/CP/Formazione/Direzione) vengono escluse.
    return isTI || isRep;
  };

  // Per ogni giorno, applica template (weekday match)
  for (const d of days) {
    const isFestivo = festivi.has(d.date) && !['sab', 'dom'].includes(d.weekday);
    for (const t of calendarTemplates || []) {
      const tw = getTemplateWeekdays(t);
      if (!tw.includes(d.weekday)) continue;
      const tFrom = calendarDayOnly(t.dateFrom);
      const tTo = calendarDayOnly(t.dateTo);
      if (tFrom && d.date < tFrom) continue;
      if (tTo && d.date > tTo) continue;
      if (isFestivo && !isAllowedOnFestivo(t)) continue;
      const isRepTpl = t.activityType === 'reperibilita' || t.timeSlot === 'reperibilita_notte' || t.timeSlot === 'reperibilita_giorno' || t.timeSlot === 'reperibilita';
      const slotDraft = {
        name: ensurePlannerSlotName(t.category, t.name, t.timeSlot, t.activityType || 'clinico'),
        category: t.category,
        activityType: t.activityType || 'clinico',
        timeSlot: t.timeSlot,
        date: d.date,
        weekday: d.weekday,
        uocId: t.uocId,
        isFestivoInfrasettimanale: isFestivo,
        hours: isRepTpl ? 12 : (t.hours ?? getHoursForSlot(t.timeSlot)),
        slotWeight: t.slotWeight ?? getSlotWeight(t.timeSlot),
      };
      const key = stableCalendarSlotKey(slotDraft);
      if (!key || slotKeys.has(key)) continue;
      slotKeys.add(key);
      slots.push({
        id: key,
        slotKey: key,
        ...slotDraft,
      });
    }

    // Modifiche perpetue: da dateFrom in poi, stesso weekday
    for (const m of calendarModifications || []) {
      if (m.modificationType !== 'add' || m.scope !== 'perpetual') continue;
      if (m.weekday !== d.weekday) continue;
      const mFrom = calendarDayOnly(m.dateFrom);
      const mTo = calendarDayOnly(m.dateTo);
      if (mFrom && d.date < mFrom) continue;
      if (mTo && d.date > mTo) continue;
      if (isFestivo && !isAllowedOnFestivo(m)) continue;
      const isRepMod = m.activityType === 'reperibilita' || m.timeSlot === 'reperibilita_notte' || m.timeSlot === 'reperibilita_giorno' || m.timeSlot === 'reperibilita';
      const modDraft = {
        name: ensurePlannerSlotName(m.category, m.name, m.timeSlot, m.activityType || 'clinico'),
        category: m.category,
        activityType: m.activityType || 'clinico',
        timeSlot: m.timeSlot,
        date: d.date,
        weekday: d.weekday,
        uocId: m.uocId,
        isFestivoInfrasettimanale: isFestivo,
        hours: isRepMod ? 12 : (m.hours ?? getHoursForSlot(m.timeSlot)),
        slotWeight: m.slotWeight ?? getSlotWeight(m.timeSlot),
      };
      const modKey = stableCalendarSlotKey(modDraft);
      if (!modKey || slotKeys.has(modKey)) continue;
      slotKeys.add(modKey);
      slots.push({
        id: modKey,
        slotKey: modKey,
        ...modDraft,
      });
    }

    // Modifiche estemporanee: data specifica
    for (const m of calendarModifications || []) {
      if (m.modificationType !== 'add' || m.scope !== 'one_off') continue;
      if (m.dateSpecific !== d.date) continue;
      if (isFestivo && !isAllowedOnFestivo(m)) continue;
      const key = `${d.date}-${m.timeSlot}-${m.category}-${m.uocId||''}-${m.id}-oneoff`;
      if (slotKeys.has(key)) continue;
      slotKeys.add(key);
      const isRepOneOff = m.activityType === 'reperibilita' || m.timeSlot === 'reperibilita_notte' || m.timeSlot === 'reperibilita_giorno' || m.timeSlot === 'reperibilita';
      slots.push({
        id: key,
        slotKey: key,
        name: ensurePlannerSlotName(m.category, m.name, m.timeSlot, m.activityType || 'clinico'),
        category: m.category,
        activityType: m.activityType || 'clinico',
        timeSlot: m.timeSlot,
        date: d.date,
        weekday: d.weekday,
        uocId: m.uocId,
        isFestivoInfrasettimanale: isFestivo,
        hours: isRepOneOff ? 12 : (m.hours ?? getHoursForSlot(m.timeSlot)),
        slotWeight: m.slotWeight ?? getSlotWeight(m.timeSlot),
        _oneOffModId: m.id,
      });
    }
    // Festivi infrasettimanali: aggiungi Rep Giorno 8-20 (UOS Blocco Operatorio)
    if (isFestivo) {
      const key = `${d.date}-reperibilita_giorno-sala_operatoria-uos_blocco_operatorio-festivo`;
      if (!slotKeys.has(key)) {
        slotKeys.add(key);
        slots.push({
          id: key,
          slotKey: key,
          name: ensurePlannerSlotName('sala_operatoria', '', 'reperibilita_giorno', 'reperibilita'),
          category: 'sala_operatoria',
          activityType: 'reperibilita',
          timeSlot: 'reperibilita_giorno',
          date: d.date,
          weekday: d.weekday,
          uocId: 'uos_blocco_operatorio',
          isFestivoInfrasettimanale: true,
          hours: 12,
          slotWeight: getSlotWeight('reperibilita_giorno'),
        });
      }
    }
  }

  // Rimuovi slot bloccati da modifiche "remove"
  const toRemoveByKey = new Set(); // perpetual: chiavi esatte
  const oneOffRemovals = []; // one_off: { dateSpecific, timeSlot, category, uocId, name } – solo template/mod, non oneoff
  for (const m of calendarModifications || []) {
    if (m.modificationType !== 'remove') continue;
    if (m.scope === 'one_off' && m.dateSpecific) {
      oneOffRemovals.push({
        dateSpecific: m.dateSpecific,
        timeSlot: m.timeSlot,
        category: m.category,
        uocId: m.uocId || '',
        name: (m.name || '').trim(),
      });
    } else if (m.scope === 'perpetual') {
      for (const s of slots) {
        if (s.weekday !== m.weekday || s.timeSlot !== m.timeSlot || s.category !== m.category) continue;
        if ((s.uocId || '') !== (m.uocId || '')) continue;
        if ((m.name || '').trim() && (s.name || '').trim() !== (m.name || '').trim()) continue;
        // Come s.date >= m.dateFrom && (!m.dateTo || s.date <= m.dateTo), con date normalizzate;
        // se dateFrom è undefined/null, in JS s >= undefined è falso → nessuna rimozione.
        if (m.dateFrom === undefined || m.dateFrom === null) continue;
        const rmFrom = String(m.dateFrom).slice(0, 10);
        const rmTo = m.dateTo != null && m.dateTo !== '' ? String(m.dateTo).slice(0, 10) : '';
        if (s.date < rmFrom) continue;
        if (rmTo && s.date > rmTo) continue;
        toRemoveByKey.add(s.slotKey || s.id);
      }
    }
  }
  const compactName = (x) => String(x || '').toLowerCase().replace(/[\s_-]+/g, '');
  const uocMatchesRemoval = (slotUoc, removalUoc, category) => {
    const su = String(slotUoc || '').trim();
    const ru = String(removalUoc || '').trim();
    if (su === ru) return true;
    const def = CATEGORY_TO_UOC[String(category || '').trim()] || '';
    // uoc vuoto sulla remove (bug storico state locale): matcha default categoria o slot senza uoc
    if (!ru) return !su || su === def;
    if (!su) return ru === def;
    return false;
  };
  const nameMatchesRemoval = (slotName, removalName) => {
    const rn = compactName(removalName);
    if (!rn) return true; // wildcard
    return compactName(slotName) === rn;
  };
  return slots.filter(s => {
    const key = s.slotKey || s.id;
    if (toRemoveByKey.has(key)) return false;
    const fromTemplateOrMod = key.includes('-lgk-') || key.endsWith('-tpl') || key.endsWith('-mod');
    if (!fromTemplateOrMod) return true; // slot one_off add non viene mai rimosso da one_off remove
    for (const r of oneOffRemovals) {
      if (s.date !== r.dateSpecific || s.timeSlot !== r.timeSlot || s.category !== r.category) continue;
      if (!uocMatchesRemoval(s.uocId, r.uocId, s.category)) continue;
      if (!nameMatchesRemoval(s.name, r.name)) continue;
      return false;
    }
    return true;
  });
}

/** Slot per date specifiche (es. una settimana) */
export function getSlotsForDates(dates) {
  const byMonth = {};
  for (const d of dates) {
    const [y, m] = d.split('-').slice(0, 2).map(Number);
    const key = `${y}-${String(m).padStart(2, '0')}`;
    if (!byMonth[key]) byMonth[key] = { year: y, month: m, dates: [] };
    byMonth[key].dates.push(d);
  }
  const allSlots = [];
  for (const { year, month, dates } of Object.values(byMonth)) {
    const slots = buildSlotsFromCalendar(year, month);
    allSlots.push(...slots.filter(s => dates.includes(s.date)));
  }
  return allSlots;
}

/**
 * Slot "ufficiali" per il Piano Turni:
 * - base calendario (template/modifiche),
 * - + slot persistiti a DB (inclusi import Excel/manuali/solver),
 * - + fallback sintetico per assegnazioni con chiave esistente ma slot non materializzato.
 */
export function getOfficialSlotsForDates(dates) {
  const dateSet = new Set((dates || []).filter(Boolean));
  const generatedSlots = getSlotsForDates([...dateSet]);
  const st = getState();
  const dbSlots = (st.shiftSlots || []).filter((s) => dateSet.has(s?.date));
  const byAssignments = st.shiftAssignmentsByKey || {};

  const syntheticSlots = [];
  const byExactKey = new Map();
  for (const s of generatedSlots) {
    const k = String(s?.slotKey || s?.id || '');
    if (k) byExactKey.set(k, true);
  }
  for (const s of dbSlots) {
    const k = String(s?.slotKey || s?.id || '');
    if (k) byExactKey.set(k, true);
  }

  for (const [k, doctorId] of Object.entries(byAssignments)) {
    if (!doctorId || byExactKey.has(k)) continue;
    const m = String(k).match(/^(\d{4}-\d{2}-\d{2})-([^-]+)-([^-]+)-([^-]+)-(.+)$/);
    if (!m) continue;
    const date = m[1];
    if (!dateSet.has(date)) continue;
    const timeSlot = m[2];
    const category = m[3];
    const uocId = m[4] || '';
    const tail = m[5] || '';
    const nameHint = tail.split('-')[0] || category;
    const dt = new Date(`${date}T12:00:00`);
    const wd = dt.getDay();
    const weekday = wd === 0 ? 'dom' : ['lun', 'mar', 'mer', 'gio', 'ven', 'sab'][wd - 1];
    const isRep = timeSlot.startsWith('reperibilita');
    const atSyn = isRep ? 'reperibilita' : (category === 'direzione' ? 'direzione' : (category === 'formazione' ? 'non_clinico' : 'clinico'));
    syntheticSlots.push({
      id: k,
      slotKey: k,
      name: ensurePlannerSlotName(category, nameHint, timeSlot, atSyn),
      category,
      activityType: atSyn,
      timeSlot,
      date,
      weekday,
      uocId,
      isFestivoInfrasettimanale: false,
      hours: isRep ? 12 : getHoursForSlot(timeSlot),
      slotWeight: getSlotWeight(timeSlot),
      syntheticFromAssignment: true,
    });
  }

  const all = [...generatedSlots, ...dbSlots, ...syntheticSlots];
  const byLogical = new Map();
  for (const s of all) {
    if (!s || !dateSet.has(s.date)) continue;
    const lk = slotLogicalKeyString(s);
    if (!lk) continue;
    if (!byLogical.has(lk)) byLogical.set(lk, []);
    byLogical.get(lk).push(s);
  }

  const out = [];
  for (const group of byLogical.values()) {
    const rep = pickRepresentativeSlotForLogicalGroup(group, byAssignments);
    if (rep) out.push(rep);
  }
  return out;
}

/** URL del solver turni: stesso server del backend (override con window.SOLVER_URL se necessario) */
const SOLVER_URL = (typeof window !== 'undefined' && window.SOLVER_URL) ||
  (typeof window !== 'undefined' && window.location && window.location.origin
    ? window.location.origin + '/calcola'
    : '/calcola');

/**
 * Trova un valore in un oggetto key->value per lo slot.
 * Usa solo chiavi esatte per evitare collisioni tra slot paralleli stessa fascia.
 */
function findValueForSlot(map, slot) {
  if (!map || typeof map !== 'object') return undefined;
  const key = slot.slotKey || slot.id;
  if (map[key] !== undefined) return map[key];
  return undefined;
}

/**
 * Restituisce true se la chiave esatta e' presente in map.
 */
function hasLockedForSlot(map, slot) {
  if (!map || typeof map !== 'object') return false;
  const key = slot.slotKey || slot.id;
  return Boolean(map[key]);
}

/** Restituisce la fase di uno slot (weekend|notti|infrasettimanali|reperibilita). Allineato a solver_orchestrator.get_slot_indices_by_phase */
export function getSlotPhase(slot) {
  const wd = slot.weekday || '';
  const ts = (slot.timeSlot || '').toLowerCase();
  const at = (slot.activityType || '').toLowerCase();
  const isRep = ts.startsWith('reperibilita') || at === 'reperibilita';
  const isWe = ['sab', 'dom'].includes(wd);
  const isNight = ts === 'notte';
  const isDiurno = ['mattino', 'pomeriggio', 'prolungato'].includes(ts);
  if (isRep) return 'reperibilita';
  if (isWe) return 'weekend';
  if (isNight) return 'notti';
  if (isDiurno) return 'infrasettimanali';
  return null;
}

/**
 * Domenica (o giorno) prima del primo giorno del periodo: notte → smonto lunedì infra.
 * @param {string} periodStart - prima data YYYY-MM-DD del periodo (o lunedì W-ISO)
 */
export function getNightRestBoundaryDate(periodStart) {
  const ps = String(periodStart || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ps)) return '';
  return addCalendarDays(ps, -1);
}

/**
 * Giorno successivo alla fine del periodo: serve alle fasi che assegnano notti (notti, weekend).
 * Una notte con inizio nell'ultimo giorno del periodo finisce alle 8 del giorno dopo, che puo'
 * essere gia' pianificato: senza questo confine il solver assegna la notte a chi ha gia' il
 * mattino successivo e l'API rifiuta l'assegnazione.
 */
export function getDiurnalRestBoundaryDate(periodEnd) {
  const pe = String(periodEnd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(pe)) return '';
  return addCalendarDays(pe, 1);
}

function _weekdayFromDateStr(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  const map = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
  return map[d.getDay()] || '';
}

/** Arricchisce previousAssignments con WE/N sul giorno limite (fuori slot periodo). */
function enrichBoundaryPreviousAssignments(byPhase, assignmentsByKey, boundaryDate, slotByKey) {
  if (!boundaryDate || !assignmentsByKey) return;
  for (const [k, docId] of Object.entries(assignmentsByKey)) {
    if (!docId) continue;
    const ks = String(k).trim();
    if (byPhase.weekend[ks] || byPhase.notti[ks] || byPhase.infrasettimanali[ks] || byPhase.reperibilita[ks]) {
      continue;
    }
    const row = slotByKey?.get(ks);
    let dateStr = '';
    let ts = '';
    let wd = '';
    if (row) {
      dateStr = (row.date || '').slice(0, 10);
      ts = String(row.timeSlot || '').toLowerCase();
      wd = row.weekday || _weekdayFromDateStr(dateStr);
    } else {
      const meta = parseCanonicalSlotKeyMeta(ks);
      if (!meta) continue;
      dateStr = meta.date;
      ts = meta.timeSlot;
      wd = _weekdayFromDateStr(dateStr);
    }
    if (dateStr !== boundaryDate) continue;
    const isRep = ts.startsWith('reperibilita');
    if (isRep) continue;
    if (ts === 'notte') {
      const ph = ['sab', 'dom'].includes(wd) ? 'weekend' : 'notti';
      byPhase[ph][ks] = docId;
    } else if (['mattino', 'pomeriggio', 'prolungato'].includes(ts) && ['sab', 'dom'].includes(wd)) {
      byPhase.weekend[ks] = docId;
    }
  }
}

/** Partiziona le assegnazioni per fase (per previousAssignmentsByPhase).
 *
 * NB: `slots` può essere filtrato alla fase corrente (es. solo infrasettimanali);
 * in quel caso le assegnazioni di altre fasi (WE+N+rep) NON sarebbero rilevate dal solo
 * loop sugli `slots`. Per non perdere le assegnazioni esistenti delle fasi precedenti
 * iteriamo anche su `assignmentsByKey` (limitato al periodo via `options.periodStart`/`periodEnd`),
 * derivando il timeSlot/weekday da `options.slotByKey` o dal parsing della chiave canonica.
 */
export function getPreviousAssignmentsByPhase(slots, assignmentsByKey, options = {}) {
  const byPhase = { weekend: {}, notti: {}, infrasettimanali: {}, reperibilita: {} };
  for (const slot of slots) {
    const key = slot.slotKey || slot.id;
    const docId = assignmentsByKey[key];
    if (!docId) continue;
    const phase = getSlotPhase(slot);
    if (phase && byPhase[phase]) byPhase[phase][key] = docId;
  }
  // Recupero assegnazioni di OTHER PHASES nel periodo (es. WE+N esistenti quando
  // `slots` contiene solo gli slot infrasettimanali della fase corrente).
  const slotByKey = options.slotByKey || null;
  const periodStart = (options.periodStart || '').slice(0, 10);
  const periodEnd = (options.periodEnd || '').slice(0, 10);
  const inRange = (ds) => {
    if (!ds) return false;
    if (periodStart && ds < periodStart) return false;
    if (periodEnd && ds > periodEnd) return false;
    return true;
  };
  for (const [keyRaw, docId] of Object.entries(assignmentsByKey || {})) {
    if (!docId) continue;
    const key = String(keyRaw);
    if (byPhase.weekend[key] || byPhase.notti[key] || byPhase.infrasettimanali[key] || byPhase.reperibilita[key]) {
      continue;
    }
    let slotInfo = slotByKey ? slotByKey.get(key) : null;
    if (!slotInfo) {
      const meta = parseCanonicalSlotKeyMeta(key);
      if (!meta) continue;
      slotInfo = {
        slotKey: key,
        date: meta.date,
        weekday: _weekdayFromDateStr(meta.date),
        timeSlot: meta.timeSlot,
      };
    }
    const ds = (slotInfo.date || '').slice(0, 10);
    if (!inRange(ds)) continue;
    const phase = getSlotPhase(slotInfo);
    if (phase && byPhase[phase]) byPhase[phase][key] = docId;
  }
  const boundaryDate = options.boundaryDate || '';
  if (boundaryDate) {
    enrichBoundaryPreviousAssignments(
      byPhase,
      assignmentsByKey,
      boundaryDate,
      options.slotByKey,
    );
  }
  return byPhase;
}

/** Forma canonica date-time_slot-cat-uoc-coda (allineata a buildSyntheticSlotFromKey nel planner). */
function parseCanonicalSlotKeyMeta(slotKey) {
  const raw = String(slotKey || '').trim();
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})-([^-]+)-([^-]+)-([^-]+)-(.+)$/);
  if (!m) return null;
  return { date: m[1], timeSlot: String(m[2] || '').toLowerCase() };
}

/**
 * Slot per chiave: calendario corrente + giorno prima del periodo (riposo post-notte su primo giorno infra)
 * + righe DB nel range [periodStart, periodEnd] e nella data precendente.
 */
function buildSlotLookupForAssignments(slots, periodStart, periodEnd, shiftSlots) {
  const byKey = new Map();
  const add = (s) => {
    if (!s) return;
    const k = s.slotKey || s.id;
    if (k) byKey.set(String(k), s);
  };
  for (const s of slots || []) add(s);

  const boundaryDates = new Set();
  if (periodStart) {
    try {
      const d = new Date(`${periodStart.slice(0, 10)}T12:00:00`);
      d.setDate(d.getDate() - 1);
      boundaryDates.add(d.toISOString().slice(0, 10));
    } catch (_) {
      /* ignore */
    }
  }
  const ps = (periodStart || '').slice(0, 10);
  const pe = (periodEnd || '').slice(0, 10);
  for (const ds of boundaryDates) {
    for (const s of getOfficialSlotsForDates([ds]) || []) add(s);
  }
  for (const s of shiftSlots || []) {
    const ds = (s?.date || '').slice(0, 10);
    if (!ds) continue;
    if (boundaryDates.has(ds) || (ps && pe && ds >= ps && ds <= pe)) add(s);
  }
  return byKey;
}

/** Notte clinica (validation.js / backend regola 9), non reperibilità notturna. */
function isClinicalNightSlotRow(row) {
  if (!row) return false;
  const ts = String(row.timeSlot || '').toLowerCase();
  const at = String(row.activityType || '').toLowerCase();
  return ts === 'notte' && at !== 'reperibilita';
}

function mergeNightDatesFromExistingAssignments(
  assignedNightDatesByDoctor,
  existingByKey,
  doctorIdsNorm,
  doctorIdAllowed,
  slotByKey,
) {
  for (const [slotKey, docIdRaw] of Object.entries(existingByKey || {})) {
    if (docIdRaw == null || docIdRaw === '') continue;
    if (!doctorIdAllowed.has(String(docIdRaw))) continue;
    const canonId = doctorIdsNorm.find(id => String(id) === String(docIdRaw));
    if (canonId == null) continue;

    let dateStr = '';
    let isNight = false;
    const meta = parseCanonicalSlotKeyMeta(slotKey);
    if (meta && meta.timeSlot === 'notte') {
      const row = slotByKey.get(String(slotKey));
      if (!row || isClinicalNightSlotRow(row)) {
        dateStr = meta.date;
        isNight = true;
      }
    }
    if (!isNight) {
      const row = slotByKey.get(String(slotKey));
      if (row && isClinicalNightSlotRow(row)) {
        dateStr = (row.date || '').slice(0, 10);
        isNight = true;
      }
    }
    // Chiavi -lgk- senza timeSlot in chiave: risoluzione solo da slotByKey (giorno limite incluso).
    if (isNight && dateStr) {
      assignedNightDatesByDoctor[canonId].push(dateStr);
    }
  }
}

/**
 * Fonte affidabile dopo refresh API: righe DB (shiftSlots) + shiftAssignmentsByKey.
 * Risolve anche chiavi DATE-lgk-* senza parsing regex e senza dipendere da lookup template.
 */
export function mergeClinicalNightAndDiurnalDatesFromPersistedRows(
  assignedNightDatesByDoctor,
  assignedDiurnalDatesByDoctor,
  shiftSlots,
  assignmentsByKey,
  doctorIdsNorm,
  doctorIdAllowed,
  periodStart,
  periodEnd,
  boundaryDate = '',
  forwardBoundaryDate = '',
) {
  const ps = (periodStart || '').slice(0, 10);
  const pe = (periodEnd || '').slice(0, 10);
  const bd = (boundaryDate || '').slice(0, 10);
  const fbd = (forwardBoundaryDate || '').slice(0, 10);
  if (ps.length !== 10 || pe.length !== 10) return;
  const byKey = assignmentsByKey || {};
  for (const row of shiftSlots || []) {
    const ds = (row.date || '').slice(0, 10);
    if (ds.length !== 10) continue;
    const inPeriod = ds >= ps && ds <= pe;
    const isBoundaryClinicalNight = bd && ds === bd && isClinicalNightSlotRow(row);
    const rowTimeSlot = String(row.timeSlot || '').toLowerCase();
    const isDiurnalRow = ['mattino', 'pomeriggio', 'prolungato'].includes(rowTimeSlot);
    // Confine in avanti: solo i diurni del giorno dopo il periodo, che vincolano la notte
    // dell'ultimo giorno. Le notti fuori periodo restano escluse, per non alterare gli altri
    // vincoli sulle notti (distanza minima, notti consecutive).
    const isBoundaryDiurnal = fbd && ds === fbd && isDiurnalRow;
    if (!inPeriod && !isBoundaryClinicalNight && !isBoundaryDiurnal) continue;
    const rk = String(row.slotKey ?? row.id ?? '').trim();
    if (!rk) continue;
    let docId = byKey[rk];
    if (docId == null || docId === '') {
      const idOnly = row.id ? String(row.id).trim() : '';
      if (idOnly) docId = byKey[idOnly];
    }
    if (docId == null || docId === '') continue;
    if (!doctorIdAllowed.has(String(docId))) continue;
    const canonId = doctorIdsNorm.find(id => String(id) === String(docId));
    if (canonId == null) continue;
    const ts = String(row.timeSlot || '').toLowerCase();
    const at = String(row.activityType || '').toLowerCase();
    if (at === 'reperibilita' || ts.startsWith('reperibilita')) continue;
    if (isClinicalNightSlotRow(row) && (inPeriod || isBoundaryClinicalNight)) {
      assignedNightDatesByDoctor[canonId].push(ds);
    }
    if (isDiurnalRow && (inPeriod || isBoundaryDiurnal)) {
      assignedDiurnalDatesByDoctor[canonId].push(ds);
    }
  }
}

/**
 * Costruisce il payload JSON per il microservizio solver.
 * Usa lookup canonica per existing e locked così le chiavi dei slot coincidono con quelle inviate al backend.
 * @param {object[]} slots - slot da calendario
 * @param {object} existingAssignmentsByKey - slotKey -> doctorId
 * @param {string} [phase] - fase singola (weekend|notti|infrasettimanali|reperibilita)
 * @param {object} [previousAssignmentsByPhase] - { weekend: {}, notti: {}, ... } da fasi precedenti
 * @returns {object} payload per POST /calcola
 */
function buildSolverPayload(slots, existingAssignmentsByKey, phase, previousAssignmentsByPhase) {
  const state = getState();
  const { doctors, lockedAssignmentsByKey, lockedWeeks, desiderata } = state;
  const dates = slots.map(s => s.date).filter(Boolean);
  const periodStart = dates.length ? dates.reduce((a, b) => (a < b ? a : b)) : '';
  const periodEnd = dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : '';
  const needsNightRestBoundary = phase === 'infrasettimanali' || phase === 'reperibilita';
  // Le fasi che assegnano notti devono conoscere i diurni gia' pianificati il giorno dopo la
  // fine del periodo: una notte dell'ultimo giorno li rende inassegnabili allo stesso medico.
  const needsDiurnalRestBoundary = phase === 'notti' || phase === 'weekend';
  const boundaryDate = needsNightRestBoundary && periodStart ? getNightRestBoundaryDate(periodStart) : '';
  const forwardBoundaryDate = needsDiurnalRestBoundary && periodEnd ? getDiurnalRestBoundaryDate(periodEnd) : '';

  const absenceDatesByDoctor = {};
  const slotBlocksByDoctorDate = {};
  for (const d of doctors || []) {
    const datesSet = getAbsenceDatesForExclusion(d.id, periodStart, periodEnd);
    absenceDatesByDoctor[d.id] = Array.from(datesSet);
    slotBlocksByDoctorDate[d.id] = {};
    for (const dateStr of [...new Set(dates)]) {
      const blocks = getSlotBlocksForDoctorDate(d.id, dateStr);
      const merged = [...new Set([...(blocks.liberaProfessione || []), ...(blocks.formazione || [])])];
      if (merged.length) slotBlocksByDoctorDate[d.id][dateStr] = merged;
    }
  }

  const existingByKey = existingAssignmentsByKey || {};
  const lockedByKey = lockedAssignmentsByKey || {};
  const payloadExisting = {};
  const payloadLocked = {};
  for (const slot of slots) {
    const key = slot.slotKey || slot.id;
    const docId = existingByKey[key] ?? findValueForSlot(existingByKey, slot);
    const weekStart = slot.date ? getWeekStart(slot.date) : '';
    const isLockedWeek = lockedWeeks && weekStart && lockedWeeks[weekStart];
    const isExplicitLock = lockedByKey[key] || hasLockedForSlot(lockedByKey, slot);
    const isLocked = Boolean(isExplicitLock || (isLockedWeek && docId));
    if (isLocked && docId) payloadExisting[key] = docId;
    if (isLocked) payloadLocked[key] = true;
  }

  // Debito/cap: solo lucchetti + esiti fasi precedenti (run a fasi); assegnazioni libere non contano.
  const debtLocked = { ...payloadLocked };
  const debtAssignments = { ...existingByKey };
  if (phase && previousAssignmentsByPhase) {
    const phaseOrder = ['weekend', 'notti', 'infrasettimanali', 'reperibilita'];
    const phaseIdx = phaseOrder.indexOf(phase);
    for (let i = 0; i < phaseIdx; i++) {
      for (const [k, docId] of Object.entries(previousAssignmentsByPhase[phaseOrder[i]] || {})) {
        if (docId) {
          debtAssignments[k] = docId;
          debtLocked[k] = true;
        }
      }
    }
  }
  const snapshotAssignments = { ...payloadExisting };
  if (phase && previousAssignmentsByPhase) {
    const phaseOrder = ['weekend', 'notti', 'infrasettimanali', 'reperibilita'];
    const phaseIdx = phaseOrder.indexOf(phase);
    for (let i = 0; i < phaseIdx; i++) {
      Object.assign(snapshotAssignments, previousAssignmentsByPhase[phaseOrder[i]] || {});
    }
  }

  /** Slot clinici intero periodo (WE+N+infra) per debito residuo e KPI spread solver. */
  const clinicalWorkloadSlots = (() => {
    if (phase !== 'infrasettimanali' || !periodStart || !periodEnd) return slots;
    const d0 = getWeekStart(periodStart);
    const dates = getDaysInWeekRange(d0, periodEnd).map(d => d.date);
    const all = getOfficialSlotsForDates(dates) || [];
    return all.filter(s => {
      const at = s.activityType || '';
      const ts = s.timeSlot || '';
      return at !== 'reperibilita' && !String(ts).startsWith('reperibilita');
    }).map(s => ({
      ...s,
      workloadLogicalKey: slotLogicalKeyString(s),
    }));
  })();
  const debtSlotList = phase === 'infrasettimanali' ? clinicalWorkloadSlots : slots;

  /** Turni clinici di sab/dom del periodo. La fase reperibilità ne ha bisogno per risolvere i
   *  legami di accorpamento weekend (rep sabato ← M+P domenica, rep domenica ← notte sabato):
   *  quelle assegnazioni arrivano da altre fasi e i loro slot non sono nel payload della fase,
   *  quindi senza questo elenco il legame salta senza alcun segnale. */
  const weekendClinicalSlots = (() => {
    if (phase !== 'reperibilita' || !periodStart || !periodEnd) return [];
    const d0 = getWeekStart(periodStart);
    const dates = getDaysInWeekRange(d0, periodEnd).map(d => d.date);
    return (getOfficialSlotsForDates(dates) || []).filter(s => {
      const at = s.activityType || '';
      const ts = String(s.timeSlot || '');
      if (at === 'reperibilita' || ts.startsWith('reperibilita')) return false;
      return ['sab', 'dom'].includes(s.weekday || '');
    });
  })();

  // Debito residuo e carico target per vincoli solver (36h/sett, target WE/notti/rep)
  const { doctorResidualDebt, festiviInfrasettimanaliByWeek } = computeDoctorResidualDebtForPeriod(
    debtSlotList, debtAssignments, debtLocked, periodStart, periodEnd
  );

  let spreadBaselineByWeek = {};
  let spreadFloorByWeek = {};
  if (phase === 'infrasettimanali' && periodStart && periodEnd && clinicalWorkloadSlots.length) {
    const baseline = computeSpreadBaselineForPeriod(
      clinicalWorkloadSlots,
      debtAssignments,
      periodStart,
      periodEnd,
    );
    spreadBaselineByWeek = baseline.spreadBaselineByWeek || {};
    spreadFloorByWeek = baseline.spreadFloorByWeek || {};
  }

  const doctorIds = (doctors || []).map(d => d.id);
  // Escludere abilitaDirezione da WE, notti, rep (conteggi e assegnazioni)
  const excludeDir = (d) => !(d.abilitaDirezione ?? d.abilita_direzione);
  const excludeWeekendExempt = (d) => !(d.esenzioneWeekend ?? d.esenzione_weekend);
  const eligibleWeekendIds = (doctors || []).filter(d => !d.specializzando && excludeDir(d) && excludeWeekendExempt(d)).map(d => d.id);
  const eligibleNightIds = (doctors || []).filter(d => !d.esenzioneNotti && !d.specializzando && excludeDir(d)).map(d => d.id);
  const eligibleRepIds = eligibleNightIds;
  const giorniPeriodo = Math.max(1, Math.ceil((new Date(periodEnd) - new Date(periodStart)) / 86400000) + 1);
  const absenceDaysInPeriodByDoctor = {};
  for (const d of doctors || []) {
    const datesSet = getFullDayAbsenceDates(d.id, periodStart, periodEnd);
    absenceDaysInPeriodByDoctor[d.id] = datesSet.size;
  }
  const wsFrom = periodStart ? getWeekStart(periodStart) : '';
  const wsTo = periodEnd ? getWeekStart(periodEnd) : '';
  let weekendDaysInPeriod = 0;
  const weekendDaysAbsentByDoctor = {};
  if (wsFrom && wsTo) {
    const daysInRange = getDaysInWeekRange(wsFrom, wsTo);
    const weekendDates = daysInRange.filter(d => d.isWeekend).map(d => d.date);
    weekendDaysInPeriod = weekendDates.length;
    for (const d of doctors || []) {
      const absentSet = getAbsenceDatesForExclusion(d.id, periodStart, periodEnd);
      weekendDaysAbsentByDoctor[d.id] = weekendDates.filter(dt => absentSet.has(dt)).length;
    }
  }
  let slotWEPeriodo = 0, weekendNightsPeriodo = 0, nottiPeriodo = 0, nottiFerialiPeriodo = 0, repPeriodo = 0;
  const weekendShapeBySat = {};
  const nightWeekStarts = new Set();
  const lockedWeekendByDoctor = {}, lockedWeekendCountByDoctor = {}, lockedWeekendNightsByDoctor = {}, lockedNightsByDoctor = {}, lockedRepByDoctor = {};
  const lockedNightsFerialiByDoctor = {};
  const lockedWeekendSatKeysByDoctor = {};
  const assignedNightDatesByDoctor = {};
  const assignedDiurnalDatesByDoctor = {};
  for (const docId of doctorIds) {
    lockedWeekendByDoctor[docId] = 0;
    lockedWeekendCountByDoctor[docId] = 0;
    lockedWeekendNightsByDoctor[docId] = 0;
    lockedNightsByDoctor[docId] = 0;
    lockedNightsFerialiByDoctor[docId] = 0;
    lockedRepByDoctor[docId] = 0;
    lockedWeekendSatKeysByDoctor[docId] = new Set();
    assignedNightDatesByDoctor[docId] = [];
    assignedDiurnalDatesByDoctor[docId] = [];
  }
  const saturdayKeyForSlot = (slot) => {
    const dateStr = (slot.date || '').slice(0, 10);
    if (!dateStr) return '';
    const d = new Date(`${dateStr}T12:00:00`);
    if (slot.weekday === 'dom') d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };
  for (const slot of slots) {
    const key = slot.slotKey || slot.id;
    const docId = payloadExisting[key];
    const isLocked = payloadLocked[key];
    const isWEDiurno = ['sab', 'dom'].includes(slot.weekday) && slot.timeSlot !== 'notte' && !(slot.activityType === 'reperibilita' || (slot.timeSlot || '').startsWith('reperibilita'));
    const isWENotte = ['sab', 'dom'].includes(slot.weekday) && slot.timeSlot === 'notte';
    const isWESlot = isWEDiurno || isWENotte; // M/P/Pr + notte sab/dom, esclusa rep (allineato a weekend_slot_indices)
    const isNight = slot.timeSlot === 'notte';
    const isNightFeriale = isNight && !['sab', 'dom'].includes(slot.weekday);
    const isRep = slot.activityType === 'reperibilita' || (slot.timeSlot || '').startsWith('reperibilita');
    if (isWESlot) {
      slotWEPeriodo++;
      const satKey = saturdayKeyForSlot(slot);
      if (satKey) {
        if (!weekendShapeBySat[satKey]) weekendShapeBySat[satKey] = { daySlots: 0, nightSlots: 0 };
        if (isWENotte) weekendShapeBySat[satKey].nightSlots += 1;
        else weekendShapeBySat[satKey].daySlots += 1;
      }
    }
    if (isWENotte) weekendNightsPeriodo++;
    if (isNight) nottiPeriodo++;
    if (isNightFeriale) {
      nottiFerialiPeriodo++;
      const ws = getWeekStart(slot.date);
      if (ws) nightWeekStarts.add(ws);
    }
    if (isRep) repPeriodo++;
    if (docId && isNight && slot.date) {
      assignedNightDatesByDoctor[docId].push(slot.date.slice(0, 10));
    }
    const isDiurnal = ['mattino', 'pomeriggio', 'prolungato'].includes(slot.timeSlot);
    if (docId && isDiurnal && slot.date) {
      assignedDiurnalDatesByDoctor[docId].push(slot.date.slice(0, 10));
    }
    if (docId && isLocked) {
      if (isWESlot) {
        lockedWeekendByDoctor[docId] = (lockedWeekendByDoctor[docId] || 0) + 1;
        const satKey = saturdayKeyForSlot(slot);
        if (satKey) lockedWeekendSatKeysByDoctor[docId].add(satKey);
      }
      if (isWENotte) lockedWeekendNightsByDoctor[docId] = (lockedWeekendNightsByDoctor[docId] || 0) + 1;
      if (isNight) lockedNightsByDoctor[docId] = (lockedNightsByDoctor[docId] || 0) + 1;
      if (isNightFeriale) lockedNightsFerialiByDoctor[docId] = (lockedNightsFerialiByDoctor[docId] || 0) + 1;
      if (isRep) lockedRepByDoctor[docId] = (lockedRepByDoctor[docId] || 0) + 1;
    }
  }
  // Notti cliniche presenti solo in mappa incarichi (slot fuori dal periodo UI, chiavi -lgk-, ecc.):
  // serve a `assignedNightDatesByDoctor` per riposo post-notte verso il primo giorno infra (solver_engine_cp.py).
  const doctorIdsNorm = doctorIds || [];
  const doctorIdAllowed = new Set(doctorIdsNorm.map(id => String(id)));
  const slotByKey = buildSlotLookupForAssignments(slots, periodStart, periodEnd, state.shiftSlots || []);
  // Con un confine attivo (indietro o avanti) servono anche le assegnazioni correnti: il turno
  // che vincola sta fuori dal periodo e nello snapshot di fase puo' non esserci.
  const boundaryMergeAssignments = (needsNightRestBoundary || needsDiurnalRestBoundary)
    ? { ...snapshotAssignments, ...existingByKey }
    : snapshotAssignments;
  mergeNightDatesFromExistingAssignments(
    assignedNightDatesByDoctor,
    boundaryMergeAssignments,
    doctorIdsNorm,
    doctorIdAllowed,
    slotByKey,
  );
  mergeClinicalNightAndDiurnalDatesFromPersistedRows(
    assignedNightDatesByDoctor,
    assignedDiurnalDatesByDoctor,
    state.shiftSlots || [],
    boundaryMergeAssignments,
    doctorIdsNorm,
    doctorIdAllowed,
    periodStart,
    periodEnd,
    boundaryDate,
    forwardBoundaryDate,
  );
  for (const docId of doctorIds) {
    lockedWeekendCountByDoctor[docId] = (lockedWeekendSatKeysByDoctor[docId] || new Set()).size;
  }
  // Stima delle "presenze weekend" totali da distribuire:
  // notti (tipicamente 1 medico per notte) + coppie di slot diurni accorpabili.
  const weekendCountPeriodo = Object.values(weekendShapeBySat).reduce(
    (acc, w) => acc + (w.nightSlots || 0) + Math.ceil((w.daySlots || 0) / 2),
    0,
  );
  const nottiWeeksPeriodo = nightWeekStarts.size;
  for (const docId of doctorIds) {
    const seen = new Set(assignedNightDatesByDoctor[docId] || []);
    assignedNightDatesByDoctor[docId] = [...seen].sort();
    const seenDiurnal = new Set(assignedDiurnalDatesByDoctor[docId] || []);
    assignedDiurnalDatesByDoctor[docId] = [...seenDiurnal].sort();
  }
  const caricoResult = computeCaricoTargetForPeriod({
    slotWEPeriodo, weekendCountPeriodo, weekendNightsPeriodo,
    nottiPeriodo, nottiFerialiPeriodo, nottiWeeksPeriodo,
    repPeriodo, giorniPeriodo, doctorIds,
    absenceDaysInPeriodByDoctor, weekendDaysInPeriod, weekendDaysAbsentByDoctor,
    eligibleWeekendIds, eligibleNightIds, eligibleRepIds,
    lockedWeekendByDoctor, lockedWeekendCountByDoctor, lockedWeekendNightsByDoctor,
    lockedNightsByDoctor, lockedNightsFerialiByDoctor, lockedRepByDoctor,
  });

  const absenceDaysByWeek = {};
  const weekStarts = getWeekStartsInRange(periodStart, periodEnd);
  for (const d of doctors || []) {
    absenceDaysByWeek[d.id] = {};
    for (const ws of weekStarts) {
      const weekEnd = getWeekEnd(ws);
      const datesSet = getFullDayAbsenceDates(d.id, ws, weekEnd);
      absenceDaysByWeek[d.id][ws] = datesSet.size;
    }
  }

  const lockedWorkSlotsByDoctorWeek = computeLockedWorkSlotsByDoctorWeek(slots, payloadExisting, payloadLocked);

  let weekendHoursOverview = {};
  let weekendPresenceHistory = {};
  if (phase === 'weekend' && periodStart && periodEnd) {
    const wsStart = getWeekStart(periodStart);
    const wsEnd = getWeekStart(periodEnd);
    const extendedStart = addCalendarDays(wsStart, -28);
    const extendedDates = getDaysInWeekRange(extendedStart, wsEnd).map(d => d.date);
    const extendedSlots = extendedDates.length
      ? getOfficialSlotsForDates(extendedDates)
      : slots;
    const historyAssignments = {
      ...(state.shiftAssignmentsByKey || {}),
      ...existingByKey,
    };
    weekendHoursOverview = computeWeekendHoursOverview(
      extendedSlots,
      historyAssignments,
      periodStart,
      periodEnd,
      4,
    );
    weekendPresenceHistory = computeWeekendPresenceHistoryByDoctor(
      extendedSlots,
      historyAssignments,
      periodStart,
      4,
    );
  }

  let crossPhaseMergeSlots = null;
  if (needsNightRestBoundary && boundaryDate) {
    const periodKeys = new Set(
      slots.map(s => String(s.slotKey || s.id || '').trim()).filter(Boolean),
    );
    const boundarySlots = (getOfficialSlotsForDates([boundaryDate]) || []).filter(s => {
      const k = String(s.slotKey || s.id || '').trim();
      return k && !periodKeys.has(k);
    });
    if (boundarySlots.length) {
      crossPhaseMergeSlots = [...slots, ...boundarySlots];
    }
  }

  const payload = {
    slots,
    doctors: doctors || [],
    softConstraints: true,
    absenceDatesByDoctor,
    slotBlocksByDoctorDate,
    desiderata: desiderata || [],
    lockedAssignmentsByKey: payloadLocked,
    lockedWeeks: lockedWeeks || {},
    existingAssignmentsByKey: payloadExisting,
    doctorResidualDebt,
    festiviInfrasettimanaliByWeek,
    caricoTable: caricoResult.caricoTable,
    absenceDaysByWeek,
    lockedWorkSlotsByDoctorWeek,
    weekendHoursOverview,
    weekendPresenceHistoryByDoctor: weekendPresenceHistory,
    assignedNightDatesByDoctor,
    assignedDiurnalDatesByDoctor,
  };
  if (phase) payload.phase = phase;
  if (previousAssignmentsByPhase) payload.previousAssignmentsByPhase = previousAssignmentsByPhase;
  if (crossPhaseMergeSlots) payload._slotsForCrossPhaseMerge = crossPhaseMergeSlots;
  if (boundaryDate) payload.nightRestBoundaryDate = boundaryDate;
  if (phase === 'reperibilita' && weekendClinicalSlots.length) {
    payload.weekendClinicalSlots = weekendClinicalSlots;
  }
  if (phase === 'infrasettimanali' && clinicalWorkloadSlots.length) {
    payload.clinicalWorkloadSlots = clinicalWorkloadSlots;
    payload.spreadBaselineByWeek = spreadBaselineByWeek;
    payload.spreadFloorByWeek = spreadFloorByWeek;
  }
  return payload;
}

/**
 * Fallback greedy: assegna slot con logica locale quando il solver CP-SAT restituisce INFEASIBLE.
 * Rispetta vincoli hard (canAssignDoctorToSlot), assegna per debito/round-robin, sempre produce una soluzione.
 */
function greedyAssignSlotsForPhase(slots, existingAssignmentsByKey, phase, prevForPhase) {
  const state = getState();
  const { doctors } = state;
  const byKey = { ...(existingAssignmentsByKey || {}) };
  for (const [k, v] of Object.entries(prevForPhase || {})) {
    if (v && typeof v === 'object') Object.assign(byKey, v);
  }
  const slotOrder = (a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    const ord = { mattino: 0, pomeriggio: 1, prolungato: 2, notte: 3 };
    const oa = ord[(a.timeSlot || '').toLowerCase()] ?? 4;
    const ob = ord[(b.timeSlot || '').toLowerCase()] ?? 4;
    return oa - ob;
  };
  const phaseSlots = slots
    .filter(s => getSlotPhase(s) === phase)
    .sort(slotOrder);
  const overtimeSlotIds = [];
  const assignedCount = {};
  for (const d of doctors || []) assignedCount[d.id] = 0;

  // TI weekend M+P stesso giorno: assegna insieme
  const processed = new Set();
  for (const slot of phaseSlots) {
    const key = slot.slotKey || slot.id;
    if (byKey[key]) continue;
    if (processed.has(key)) continue;

    const isTIWeekendMP = slot.category === 'terapia_intensiva' && ['sab', 'dom'].includes(slot.weekday) &&
      (slot.timeSlot === TIME_SLOTS.MATTINO || slot.timeSlot === TIME_SLOTS.POMERIGGIO);
    let slotsToAssign = [slot];
    if (isTIWeekendMP) {
      const otherTime = slot.timeSlot === TIME_SLOTS.MATTINO ? TIME_SLOTS.POMERIGGIO : TIME_SLOTS.MATTINO;
      const twin = phaseSlots.find(s =>
        s.date === slot.date && s.timeSlot === otherTime && s.category === 'terapia_intensiva'
      );
      if (twin && !byKey[twin.slotKey || twin.id]) {
        slotsToAssign = [slot, twin];
        processed.add(twin.slotKey || twin.id);
      }
    }

    const opts = {
      slots,
      assignmentsByKey: byKey,
      allowStraordinario: true,
      allowDirectorTamponage: true,
      allowOverrideSameDayMPForLeveling: true,
    };
    const candidates = (doctors || []).filter(d => {
      if (!d.id || d.specializzando) return false;
      const sim = { ...byKey };
      for (const s of slotsToAssign) sim[s.slotKey || s.id] = d.id;
      return slotsToAssign.every(s => canAssignDoctorToSlot(d.id, s, { ...opts, assignmentsByKey: sim }).ok);
    });
    if (candidates.length === 0) {
      for (const s of slotsToAssign) overtimeSlotIds.push(s.id || s.slotKey);
      continue;
    }
    candidates.sort((a, b) => (assignedCount[a.id] || 0) - (assignedCount[b.id] || 0));
    const chosen = candidates[0];
    for (const s of slotsToAssign) {
      const k = s.slotKey || s.id;
      if (k) byKey[k] = chosen.id;
      assignedCount[chosen.id] = (assignedCount[chosen.id] || 0) + 1;
    }
  }

  const doctorHours = {};
  for (const d of doctors || []) {
    doctorHours[d.id] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0 };
  }
  for (const slot of phaseSlots) {
    const docId = byKey[slot.slotKey || slot.id];
    if (!docId || !doctorHours[docId]) continue;
    const h = getHoursForSlot(slot.timeSlot, slot.hours) || 6;
    if (['sab', 'dom'].includes(slot.weekday) && slot.timeSlot !== 'notte') doctorHours[docId].weekend += h;
    else if (slot.timeSlot === 'notte') doctorHours[docId].notturne += h;
    else if (slot.activityType === 'reperibilita' || (slot.timeSlot || '').startsWith('reperibilita')) doctorHours[docId].reperibilita += h;
    else doctorHours[docId].feriali += h;
  }

  return {
    assignments: byKey,
    doctorHours,
    overtimeSlotIds,
    levelingDiagnostics: [
      'Fallback greedy: il solver CP-SAT era INFEASIBLE; assegnazione eseguita con logica locale.',
      overtimeSlotIds.length ? `${overtimeSlotIds.length} slot non assegnati (nessun medico idoneo).` : 'Tutti gli slot coperti.',
    ],
    assignmentDiagnostics: {},
    phase,
  };
}

/**
 * Assegnazione automatica per una singola fase (weekend|notti|infrasettimanali|reperibilita).
 * Usa le assegnazioni esistenti delle fasi precedenti come locked.
 *
 * @param {object[]} slots - slot da calendario
 * @param {object} [existingAssignmentsByKey] - slotKey -> doctorId già assegnati
 * @param {string} phase - weekend|notti|infrasettimanali|reperibilita
 * @returns {Promise<{assignments, doctorHours, overtimeSlotIds, levelingDiagnostics, assignmentDiagnostics}>}
 */
export async function autoAssignSlotsForPhase(slots, existingAssignmentsByKey, phase) {
  const dates = (slots || []).map(s => s.date).filter(Boolean);
  const periodStart = dates.length ? dates.reduce((a, b) => (a < b ? a : b)) : '';
  const periodEnd = dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : '';
  const boundaryDate =
    (phase === 'infrasettimanali' || phase === 'reperibilita') && periodStart
      ? getNightRestBoundaryDate(periodStart)
      : '';
  const state = getState();
  const slotByKey = buildSlotLookupForAssignments(
    slots,
    periodStart,
    periodEnd,
    state.shiftSlots || [],
  );
  const prev = getPreviousAssignmentsByPhase(slots, existingAssignmentsByKey, {
    boundaryDate,
    slotByKey,
    periodStart,
    periodEnd,
  });
  const phaseOrder = ['weekend', 'notti', 'infrasettimanali', 'reperibilita'];
  const idx = phaseOrder.indexOf(phase);
  const prevForPhase = {};
  for (let i = 0; i < idx; i++) prevForPhase[phaseOrder[i]] = prev[phaseOrder[i]];
  const payload = buildSolverPayload(slots, existingAssignmentsByKey, phase, prevForPhase);
  const timeoutMs = solverRequestTimeoutMs(slots.length, phase);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const solveStartedAt = Date.now();
  const res = await fetch(SOLVER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: controller.signal,
  });
  clearTimeout(timeoutId);
  if (!res.ok) {
    let errDetail = res.status === 504
      ? solver504Message(Date.now() - solveStartedAt)
      : 'Impossibile generare il turnario (vincoli incoerenti).';
    try {
      const body = await res.json();
      if (body.detail) {
        const d = body.detail;
        if (typeof d === 'string') {
          errDetail = d;
        } else if (Array.isArray(d)) {
          errDetail = d.map(x => x.msg || x.message || JSON.stringify(x)).slice(0, 3).join('; ');
        } else if (d.diagnostics && d.diagnostics.length) {
          errDetail = (d.error || 'INFEASIBLE') + ': ' + d.diagnostics.join('; ');
        } else if (d.error) {
          errDetail = d.error + (d.diagnostics?.length ? ': ' + d.diagnostics.join('; ') : '');
        }
      }
    } catch (_) {}
    throw new Error(`Solver ${res.status}: ${errDetail}`);
  }
  const result = await res.json();
  const diag = result.levelingDiagnostics || [];
  return {
    assignments: result.assignments || {},
    doctorHours: result.doctorHours || {},
    overtimeSlotIds: result.overtimeSlotIds || [],
    levelingDiagnostics: diag,
    assignmentDiagnostics: result.assignmentDiagnostics || {},
    autoUnlockedSlotIds: result.autoUnlockedSlotIds || [],
    phase: result.phase,
  };
}

/**
 * Assegnazione automatica tramite microservizio Python (OR-Tools CP-SAT).
 * Delegazione al solver: assenze, copertura esatta, equità, massimizzazione competenze.
 *
 * @param {object[]} slots - slot da calendario
 * @param {object} [existingAssignmentsByKey] - slotKey -> doctorId già assegnati (da preservare)
 * @returns {Promise<{assignments, doctorHours, overtimeSlotIds, levelingDiagnostics, assignmentDiagnostics}>}
 */
export async function autoAssignSlots(slots, existingAssignmentsByKey = {}) {
  const state = getState();
  const { doctors } = state;
  if (!slots || slots.length === 0) {
    return {
      assignments: {},
      doctorHours: Object.fromEntries((doctors || []).map(d => [d.id, { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0 }])),
      overtimeSlotIds: [],
      levelingDiagnostics: ['Nessuno slot da assegnare.'],
      assignmentDiagnostics: {},
    };
  }
  if (!doctors || doctors.length === 0) {
    return {
      assignments: {},
      doctorHours: {},
      overtimeSlotIds: slots.map(s => s.id),
      levelingDiagnostics: ['Nessun medico disponibile.'],
      assignmentDiagnostics: {},
    };
  }

  const payload = buildSolverPayload(slots, existingAssignmentsByKey);

  const timeoutMs = solverRequestTimeoutMs(slots.length, payload.phase || null);

  let solveStartedAt = 0;
  async function trySolve(p) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    solveStartedAt = Date.now();
    const res = await fetch(SOLVER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(p),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return res;
  }

  try {
    const res = await trySolve(payload);

    if (!res.ok) {
      let errDetail = res.status === 504
        ? solver504Message(Date.now() - solveStartedAt)
        : 'Impossibile generare il turnario (vincoli incoerenti).';
      try {
        const body = await res.json();
        if (res.status === 422 && body.detail && typeof body.detail === 'object') {
          const d = body.detail;
          const lines = [
            'Impossibile generare il turnario (vincoli incoerenti).',
            ...(d.diagnostics || []),
            ...(d.slotWithoutCandidates?.length ? [`Slot senza candidati: ${d.slotWithoutCandidates.slice(0, 5).join(', ')}${d.slotWithoutCandidates.length > 5 ? '...' : ''}`] : []),
            ...(d.suggestedChecks || []),
          ];
          return {
            assignments: {},
            doctorHours: Object.fromEntries((doctors || []).map(d => [d.id, { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0 }])),
            overtimeSlotIds: slots.map(s => s.id),
            levelingDiagnostics: lines,
            assignmentDiagnostics: d.assignmentDiagnostics || {},
          };
        }
        if (body.detail) {
          const d = body.detail;
          if (Array.isArray(d)) errDetail = d.map(x => x.msg || x.message || JSON.stringify(x)).slice(0, 3).join('; ');
          else if (d.diagnostics?.length) errDetail = (d.error || 'INFEASIBLE') + ': ' + d.diagnostics.join('; ');
          else if (typeof d === 'string') errDetail = d;
          else if (d.error) errDetail = d.error + (d.diagnostics?.length ? ': ' + d.diagnostics.join('; ') : '');
          else errDetail = JSON.stringify(d);
        }
      } catch (_) {}
      throw new Error(`Solver ${res.status}: ${errDetail}`);
    }

    const result = await res.json();
    const diag = result.levelingDiagnostics || [];
    return {
      assignments: result.assignments || {},
      doctorHours: result.doctorHours || {},
      overtimeSlotIds: result.overtimeSlotIds || [],
      levelingDiagnostics: diag,
      assignmentDiagnostics: result.assignmentDiagnostics || {},
      autoUnlockedSlotIds: result.autoUnlockedSlotIds || [],
    };
  } catch (err) {
    const timeoutSec = Math.round(solverRequestTimeoutMs(slots.length, payload.phase || null) / 1000);
    const msg = err.name === 'AbortError' ? `Timeout: il solver non ha risposto in ${timeoutSec} secondi.` : err.message;
    const isNetwork = /fetch|network|failed|refused|timeout|ERR_|enotfound/i.test(msg);
    const hint = typeof window !== 'undefined' && window.location && window.location.origin
      ? ` Apri l'app da ${window.location.origin}`
      : '';
    return {
      assignments: {},
      doctorHours: Object.fromEntries((doctors || []).map(d => [d.id, { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0 }])),
      overtimeSlotIds: slots.map(s => s.id),
      levelingDiagnostics: [
        isNetwork ? 'Errore di rete: il microservizio non è raggiungibile.' : `Errore solver: ${msg}`,
        'Verifica che il backend sia avviato (./start_server.sh).' + hint,
        ...(isNetwork && msg ? [`Dettaglio: ${msg}`] : []),
      ],
      assignmentDiagnostics: {},
    };
  }
}
